import { useMemo } from 'react';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';

import { ManifestPhoto } from 'api/assetManifest';

/**
 * Shuffle a copy, Fisher-Yates.
 *
 * The galleries have always appeared in a random order, and that is preserved
 * here — but the previous implementation was `sort(() => Math.random() - 0.5)`,
 * which is not a valid comparator. It is inconsistent between calls on the same
 * pair, so the result is neither uniformly random nor, strictly, defined at
 * all; V8 is entitled to do anything with it. This is the same intent, actually
 * implemented.
 */
const shuffled = <T,>(items: readonly T[]): T[] => {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
};

interface PhotoGalleryProps {
    photos: ManifestPhoto[];
}

/**
 * Renders a set of photos, or nothing at all.
 *
 * Nothing is a legitimate and common answer: an event whose photos have not
 * been taken yet, and — until the assets bucket is synced and
 * `VITE_ASSETS_BASE_URL` is set — every event.
 */
export const PhotoGallery = ({ photos }: PhotoGalleryProps) => {
    const items = useMemo(
        () =>
            shuffled(photos).map((photo) => ({
                original: photo.display,
                thumbnail: photo.thumb,
                originalHeight: 600,
            })),
        [photos],
    );
    return items.length > 0 ? <ImageGallery items={items} lazyLoad /> : null;
};
